import videojs from 'video.js';

import { loadPresentationData, overrideServerSettings, ensurePresentationAvailable, applyLoaderCallbacks, applyPlayerSource, applyPlayerOptions } from './loader/';
import { isPresentationId } from '../../util';
import { getPosterInfo, PresentationStatus } from '../../api/presentation';
import { reportMediaOpen } from '../../api/player';

const _options = new WeakMap();
const _presentationId = new WeakMap();

const _playCoverData = new WeakMap();
const _presentationData = new WeakMap();

const _playCoverPromise = new WeakMap();
const _presentationPromise = new WeakMap();

const _playCoverCallbacks = new WeakMap();
const _presentationCallbacks = new WeakMap();

/**
 * The Loader module.
 * @member {LoaderPlugin} loader
 * @memberof MediasitePlayer#
 */

/**
 * Provides methods to assist with the loading of Mediasite data from the server.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class LoaderPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        _playCoverCallbacks.set(this, []);
        _presentationCallbacks.set(this, []);

        const { presentationId, ...initOptions } = options;
        if (presentationId) {
            this.init(presentationId, initOptions);
        }
    }

    /**
     * The ID of the Mediasite presentation associated with this player
     * @type {string}
     * @example
     * const presentationId = player.loader.presentationId;
     */
    get presentationId() {
        return _presentationId.get(this);
    }

    /**
     * A boolean indicating whether or not the play cover has been loaded yet.
     * @type {boolean}
     * @example
     * const isPlayCoverLoaded = player.loader.playCoverLoaded;
     */
    get playCoverLoaded() {
        return !(!_playCoverData.get(this));
    }

    /**
     * A boolean indicating whether or not the presentation has been loaded yet and the play cover dismissed.
     * @type {boolean}
     * @example
     * const isPresentationLoaded = player.loader.presentationLoaded;
     */
    get presentationLoaded() {
        return !(!_presentationData.get(this));
    }

    /**
    * Sets up the player for the given presentation and begins loading the play cover data.
    * @param {string} presentationId The ID of the presentation to load.
    * @param {Object} [options]
    * @param {number} [options.playFrom] A time in seconds at which to begin playback.
    * @param {Object} [options.overrides]
    * @private
    * @returns {Promise} A Promise that is resolved with the play cover data when the play cover has finished loading.
    */
    init(presentationId, options = {}) {
        let playCoverPromise = _playCoverPromise.get(this);

        if (!playCoverPromise) {
            this.log(`Attempting to load play cover data for presentation with ID: ${presentationId}`);
            this.player.addClass('vjs-waiting');

            _presentationId.set(this, presentationId);
            _options.set(this, options);

            const playCoverCallbacks = _playCoverCallbacks.get(this);
            playCoverPromise = getPosterInfo(this.presentationId).then(async (playCoverData) => {
                this.player.metadata().title = playCoverData.title;
                this.player.playcover().visible = options.overrides?.playcover?.visible !== false;

                // Todo: include this in the playCoverCallbacks so that loading is delayed until the poster image has been loaded over the network
                const posterPromise = new Promise((resolve, reject) => {
                    if (!playCoverData.poster) {
                        reject();
                    } else {
                        const img = document.createElement('img');
                        img.addEventListener('load', () => {
                            this.player.poster(playCoverData.poster);
                            resolve();
                        });
                        img.src = playCoverData.poster;
                    }
                }).catch(() => {
                    this.log('There was not a valid playcover poster to display.');
                });

                const { playerOptions } = await applyLoaderCallbacks(this.player, this.presentationId, playCoverData, { playerOptions: _options.get(this)?.overrides ?? {} }, playCoverCallbacks);
                applyPlayerOptions(this.player, playerOptions);

                _playCoverData.set(this, playCoverData);
                this.player.removeClass('vjs-waiting');
                this.player.trigger('playcoverloaded');
                this.log('Finished loading play cover data.');

                if (options.overrides?.playcover?.visible === false) {
                    this.loadPresentation();
                }

                if (isPresentationId(this.presentationId) && playCoverData.playStatus === PresentationStatus.NotAvailable) {
                    this.player.playcover().showPlayButton = false;
                    this.player.removeClass('vjs-waiting');
                    this.player.showMessage('The presentation is currently unavailable. Please try again later.', { uncloseable: true });
                }

                return playCoverData;
            });

            _playCoverPromise.set(this, playCoverPromise);
        }

        return playCoverPromise;
    }


    applyLiveWait(presentationData) {
        const streams = presentationData.streams;
        if (presentationData?.metadata?.playStatus === 'OnDemand' || presentationData?.metadata?.playStatus === 'NotAvailable' || presentationData?.metadata?.playStatus == null) {
            return Promise.resolve(presentationData);
        } else {
            this.player.live().segmentLength = streams.find(src => this.player.canPlayType(src?.items[0]?.type))?.items[0]?.segmentLength ?? this.player.live()?.segmentLength;
            return this.player.live().waitUntilSafe().then(() => {
                return Promise.resolve(presentationData);
            });
        }
    }


    /**
    * Begins loading the presentation, requesting the needed data from the server in the process.
    * @returns {Promise<PresentationData>} A Promise that is resolved when the presentation has finished loading and the play cover has been dismissed.
    * @example
    * player.loader.loadPresentation().then(() => {
    *     console.log('Presentation has been loaded, and the play cover has been dismissed.');
    * });
    */
    loadPresentation() {
        let presentationPromise = _presentationPromise.get(this);

        if (!presentationPromise) {
            this.log(`Attempting to load presentation data for presentation with ID: ${this.presentationId}`);
            this.player.addClass('vjs-waiting');

            const presentationCallbacks = _presentationCallbacks.get(this);
            presentationPromise = loadPresentationData(this.presentationId)
                .then(data => overrideServerSettings(data, _options.get(this)?.overrides))
                .then(ensurePresentationAvailable)
                .then(data => this.applyLiveWait(data))
                .then(data => applyLoaderCallbacks(this.player, this.presentationId, _playCoverData.get(this), data, presentationCallbacks))
                .then(data => applyPlayerSource(this.player, data)).then(async (presentationData) => {
                    const duration = this.player.duration();
                    this.initBookmarkStorage(duration, presentationData.metadata.serverClockSkew);
                    const playFrom = _options.get(this).playFrom ?? (presentationData.metadata.bookmark || this.getStoredBookmarkPosition());
                    //playFrom=0 should seek to live edge for live presentations
                    if (playFrom === 0 && (presentationData.metadata.playStatus === PresentationStatus.Live || presentationData.metadata.playStatus === PresentationStatus.LivePaused)) {
                        this.player.getChild('LiveTracker')?.seekToLiveEdge();
                    }
                    else if (playFrom === Infinity) {
                        this.player.getChild('LiveTracker').seekToLiveEdge();
                    }
                    //move the player's current time to the playfrom time as long as quizzing's seeking is not disabled
                    else if (playFrom >= 0 && playFrom !== Math.round(duration) && (this.player.quizzes?.().enableSeeking != false)) {
                            this.player.currentTime(playFrom);
                    }

                    //if the duration parameter has been set, pause the video after the amount of time stated in the parameter
                    let durationPause = _options.get(this).duration;

                    if (durationPause) {
                        //offset the pausing by where the video is starting to play from
                        //either through the PlayFrom parameter or bookmarking
                        if (playFrom && playFrom !== Infinity && playFrom + durationPause < duration) {
                            durationPause += playFrom;
                        }

                        this.player.timedEvents().add({
                            id: 'duration-pause',
                            time: durationPause,
                            duration: 1,
                            type: 'hidden'
                        }, () => this.player.pause());
                    }

                    this.player.bookmarkStorage().lastStartTime = playFrom >= 0 ? playFrom : 0;
                    this.player.inactivity().sessionTimeout = presentationData.playerOptions?.inactivity?.sessionTimeout;
                    const playbackTicket = this.player.reporting().playbackTicket;
                    if (playbackTicket) {
                        reportMediaOpen(playbackTicket);
                    }
					this.player.reporting().reportToDataProvider(this.player.reporting().playbackTicket);
                    this.player.reporting().trackPresentation();
                    
                    this.player.removeClass('vjs-waiting');
                    _presentationData.set(this, presentationData);
                    this.player.trigger('presentationloaded');
                    this.log('Finished loading presentation data.');
                    return presentationData;
                });

            _presentationPromise.set(this, presentationPromise);
        }

        return presentationPromise;
    }

    /**
     * @callback LoaderCallback
     * @param {string} presentationId
     * @param {PresentationData} presentationData
     * @param {PlayCoverData} playCoverData
     */

    /**
     * Registers a callback that will be passed the play cover information once loading has completed, but before the "loading" spinner has been dismissed.
     * @param {LoaderCallback} callback
     */
    onLoadPlayCover(callback) {
        _playCoverCallbacks.get(this).push(callback);
    }

    /**
     * Registers a callback that will be passed the presentation information once loading has completed, but before the play cover has been dismissed or playback begun.
     * @param {LoaderCallback} callback
     */
    onLoadPresentation(callback) {
        _presentationCallbacks.get(this).push(callback);
    }

    /**
     * Initializes the bookmarkStorage plugin by setting presentation Id, duration, and server clock skew.
     * @param {number} duration
     * @param {number} skew
     */
    initBookmarkStorage(duration, skew) {
        if (!this.player.reporting().includeBookmark) {
            return;
        }
        const storage = this.player.bookmarkStorage();
        storage.presentationId = this.presentationId;
        storage.presentationDuration = duration;
        storage.clockSkew = skew;
    }

    /**
     * Gets the position in seconds from any locally stored bookmark.
     * @returns {number} the position, in seconds, to start playback from
     */
    getStoredBookmarkPosition() {
        if (!this.player.reporting().includeBookmark) {
            return 0;
        }
        const storage = this.player.bookmarkStorage();
        return storage.retrieve().position ?? 0;
    }
}

export default LoaderPlugin;
